import { memo } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import type { FluxmedDefaultTemplateCategory } from "../utils/messageTemplateUiUtils";
import { getFluxmedDefaultCategoryAccent } from "../utils/fluxmedDefaultTemplateCategoryIcons";

export interface MessageTemplateCardIconGroupProps {
  CategoryIcon?: SvgIconComponent;
  category?: FluxmedDefaultTemplateCategory;
  categoryLabel?: string;
}

const WHATSAPP_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: 1.5,
  flexShrink: 0,
  backgroundColor: "rgba(37, 211, 102, 0.12)",
  display: "grid",
  placeItems: "center",
} as const;

const MessageTemplateCardIconGroup = memo(
  ({ CategoryIcon, category, categoryLabel }: MessageTemplateCardIconGroupProps) => {
    const theme = useTheme();
    const accent =
      category != null ? getFluxmedDefaultCategoryAccent(category, theme) : null;

    const whatsappIcon = (
      <Box sx={WHATSAPP_ICON_BOX_SX}>
        <WhatsAppIcon sx={{ color: "#25D366", fontSize: 22 }} />
      </Box>
    );

    if (!CategoryIcon || !accent) {
      return whatsappIcon;
    }

    const categoryBadgeSx = {
      position: "absolute" as const,
      right: -2,
      bottom: -2,
      width: 22,
      height: 22,
      borderRadius: "50%",
      bgcolor: accent.lightBg,
      border: "2px solid",
      borderColor: "background.paper",
      boxShadow: `0 1px 4px ${accent.main}33`,
      display: "grid",
      placeItems: "center",
      color: accent.main,
    };

    const categoryBadge = (
      <Box component="span" sx={categoryBadgeSx}>
        <CategoryIcon sx={{ fontSize: 13 }} />
      </Box>
    );

    return (
      <Box
        sx={{
          position: "relative",
          width: 40,
          height: 40,
          flexShrink: 0,
          mr: 0.5,
        }}
      >
        {whatsappIcon}
        {categoryLabel ? (
          <Tooltip title={categoryLabel} placement="top" arrow>
            {categoryBadge}
          </Tooltip>
        ) : (
          categoryBadge
        )}
      </Box>
    );
  },
);

MessageTemplateCardIconGroup.displayName = "MessageTemplateCardIconGroup";

export default MessageTemplateCardIconGroup;
